import type { PlaneOrientation, WorldPos } from "./types";

export interface NavPort {
  /** Canonical IPP for a list index from studyMetadata — NOT cornerstone.metaData. */
  ippFor(listIndex: number): WorldPos | null;
  /** +1 if increasing list-index moves anatomically up, else -1 (from listAnatomicSign). */
  anatomicSign(): 1 | -1;
  totalSlices(): number;
  orientation(): PlaneOrientation;
  /** True when the strategy jumps to a world position losslessly (volume:
   *  jumpToWorld). False for stacks, whose only world nav is a lossy
   *  world→nearest-index projection — so our own list-index navigation must go
   *  straight to showIndex instead of round-tripping through an IPP (which
   *  collapses irregular/multi-slab geometry onto ~1 index per cluster). */
  navigatesByWorld(): boolean;
  showWorld(world: WorldPos, orientation: PlaneOrientation): void;
  /** Direct list-index nav (stacks) and the IPP-missing fallback (volumes). */
  showIndex(listIndex: number): void;
  onWarn(code: string, detail: Record<string, unknown>): void;
}

export class NavigationController {
  constructor(private port: NavPort) {}

  navigateToIndex(listIndex: number): void {
    const i = this.clamp(listIndex);
    // Volumes jump to the slice's world position (jumpToWorld) so panes stay
    // anatomically synced. Stacks have no lossless world jump — their only
    // world nav is a world→nearest-index projection, which collapses irregular
    // / multi-slab geometry onto ~1 index per cluster. So for a stack, drive
    // our own authoritative list-index straight to showIndex.
    if (this.port.navigatesByWorld()) {
      const ipp = this.port.ippFor(i);
      if (ipp) {
        this.port.showWorld(ipp, this.port.orientation());
        return;
      }
      this.port.onWarn("viewer_slice_jump_index_fallback", { listIndex: i });
    }
    this.port.showIndex(i);
  }

  step(wheelUp: number, currentIndex: number): void {
    // wheelUp: +1 = user scrolled up; anatomicSign maps that to an index delta.
    const delta = wheelUp * this.port.anatomicSign();
    this.navigateToIndex(currentIndex + delta);
  }

  private clamp(i: number): number {
    return Math.max(0, Math.min(this.port.totalSlices() - 1, i));
  }
}
